import {useAppSelector} from '../store/hooks';
import {
  currentProjectIdSelector,
  projectDataSelector,
  projectsHaveLoadedSelector,
} from '../store/selectors/projects.selectors';
import type {Project} from '../domain/types';

export interface ProjectScope {
  /** The project id it is safe to send to the API. `undefined` means "no project" — fetch unscoped. */
  projectId: string | undefined;
  /** The project record, when the requested project is one of this account's. */
  project: Project | undefined;
  /**
   * Ownership is not decidable yet. Callers **must not** fetch while this is true — it is the state
   * where a project id exists but has not been checked against anything.
   */
  isResolving: boolean;
  /** A project was asked for and it provably is not this account's. */
  isForeign: boolean;
}

/**
 * Resolves "which project are we looking at" to one this account actually owns — or to nothing.
 *
 * **The client must never send a project id it cannot vouch for.** Emptying the store on a user
 * change (see `sessionIdentityMiddleware`) fixes the ids that come from Redux and localStorage, but
 * not the one in the address bar: `/projects/<previous-account-id>/workspace` stays in the URL
 * across a sign-in, is in browser history, and is what people bookmark and paste to each other. A
 * route param is untrusted input, and sending it unchecked is what produced
 * `403 Unauthorized: You do not own this project`.
 *
 * So the id is checked against the project list fetched for the *current* session before any request
 * uses it. `hasFetched` is what makes that check meaningful: an empty list means "not asked yet"
 * until a fetch has settled, and answering during that window is how you end up guessing.
 *
 * Not owning the project is a normal outcome, not an error — a signed-out link, a shared URL, an
 * account switch. Callers fall back to the unscoped view; `isForeign` is there for the ones that
 * want to correct the URL as well.
 */
export function useOwnedProjectId(routeProjectId?: string): ProjectScope {
  const reduxProjectId = useAppSelector(currentProjectIdSelector);
  const projects = useAppSelector(projectDataSelector);
  const projectsLoaded = useAppSelector(projectsHaveLoadedSelector);

  // The URL wins over the remembered project — it is what the user is looking at.
  const requestedId = routeProjectId || reduxProjectId || undefined;

  if (!requestedId) {
    return {projectId: undefined, project: undefined, isResolving: false, isForeign: false};
  }

  const project = projects.find(candidate => candidate.id === requestedId);
  if (project) {
    return {projectId: requestedId, project, isResolving: false, isForeign: false};
  }

  if (!projectsLoaded) {
    return {projectId: undefined, project: undefined, isResolving: true, isForeign: false};
  }

  return {projectId: undefined, project: undefined, isResolving: false, isForeign: true};
}
